// src/screens/BillPrintPage.jsx
import React, { useEffect, useState } from "react";
import PageSerialBadge from "../components/PageSerialBadge";

/**
 * Professional invoice print page styled to match the uploaded sample.
 * Reads payload from sessionStorage key "billing_print_data".
 *
 * Expected payload shape (example):
 * {
 *  company: { name, address, gstin, phone, place, logoUrl },
 *  voucherNo, invoiceDate, invoiceNo,
 *  party: { partyName, address, gstin, phone },
 *  consignee: {...} (optional),
 *  items: [{ sl, code, desc, hsn, qty, uom, unitPrice, gross, discountPercent, discountAmt, taxableValue, gstPercent, gstAmt, total }],
 *  totals: { subtotal, taxable, cgst, sgst, igst, roundOff, grandTotal },
 *  amountWords,
 *  bank: { name, acctName, acctNo, branch, ifsc },
 *  notes, isInterState, payment
 * }
 */

export default function BillPrintPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("billing_print_data");
      if (raw) setData(JSON.parse(raw));
      else if (window.opener && window.opener.__billing_print_data__) setData(window.opener.__billing_print_data__);
    } catch (e) {
      console.error("Failed to load print data", e);
    }
  }, []);

  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [data]);

  if (!data) {
    return (
      <div className="relative" style={{ padding: 20, fontFamily: "Arial, sans-serif" }}>
        <div className="no-print" style={{ position: "absolute", right: 16, top: 16 }}>
          <PageSerialBadge variant="plain" />
        </div>
        <h3>No invoice data found</h3>
        <p>Please generate invoice from Outward Supply and click Print Bill.</p>
      </div>
    );
  }

  const {
    company = {},
    voucherNo = "",
    invoiceDate = "",
    invoiceNo = "",
    party = {},
    consignee = {},
    items = [],
    totals = {},
    amountWords = "",
    bank = {},
    notes = "",
    isInterState = false,
    payment = {},
  } = data;

  const money = (v) => `₹ ${Number(v || 0).toFixed(2)}`;
  const small = { fontSize: 12, color: "#222" };

  return (
    <div style={{ fontFamily: "Arial, sans-serif", color: "#111", padding: "10mm", position: "relative" }}>
      <div className="no-print" style={{ position: "absolute", right: 0, top: 0, zIndex: 2 }}>
        <PageSerialBadge variant="plain" />
      </div>
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          body { -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
          thead { display: table-header-group !important; }
          tfoot { display: table-row-group !important; }
          tr { page-break-inside: avoid !important; }
        }
        .inv-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .inv-table th, .inv-table td { border: 1px solid #dfe3e8; padding: 6px; vertical-align: top; }
        .inv-table thead th { background: #f7f9fb; font-weight:700; }
        .right { text-align: right; }
        .center { text-align: center; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ maxWidth: 520 }}>
          {company.logoUrl ? (
            <img src={company.logoUrl} alt="logo" style={{ maxHeight: 70, objectFit: "contain" }} />
          ) : null}
          <div style={{ fontSize: 18, fontWeight: 800 }}>{company.name || "Company Name"}</div>
          <div style={{ marginTop: 6, ...small }}>{company.address || ""}</div>
          <div style={{ marginTop: 4, ...small }}>Place: {company.place || ""}</div>
          <div style={{ marginTop: 4, ...small }}>Phone: {company.phone || ""} Email: {company.email || ""}</div>
          <div style={{ marginTop: 4, ...small }}>GSTIN: {company.gstin || "-"}</div>
        </div>

        <div style={{ minWidth: 260, textAlign: "right" }}>
          <div style={{ fontSize: 16, fontWeight: 800, border: "1px solid #e8eaef", padding: "8px 12px", background: "#fafbfd" }}>TAX INVOICE</div>
          <div style={{ marginTop: 10, ...small }}>
            <div><strong>Invoice No:</strong> {invoiceNo || voucherNo}</div>
            <div><strong>Invoice Date:</strong> {invoiceDate}</div>
            <div><strong>Payment:</strong> {payment.mode || "Cash"}</div>
            <div><strong>Place of Supply:</strong> {company.place || ""}</div>
          </div>
        </div>
      </div>

      {/* Billed to / Consignee */}
      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <div style={{ flex: 1, border: "1px solid #e8e8e8", padding: 8 }}>
          <div style={{ fontWeight: 700 }}>Details of Receiver (Billed to)</div>
          <div style={{ marginTop: 6, ...small }}>{party.partyName || "-"}</div>
          <div style={{ marginTop: 4, ...small }}>{party.address || ""}</div>
          <div style={{ marginTop: 4, ...small }}>GSTIN: {party.gstin || "-"}</div>
          <div style={{ marginTop: 4, ...small }}>PH: {party.phone || ""}</div>
        </div>

        <div style={{ flex: 1, border: "1px solid #e8e8e8", padding: 8 }}>
          <div style={{ fontWeight: 700 }}>Details of Consignee (Shipped to)</div>
          <div style={{ marginTop: 6, ...small }}>{consignee.partyName || "-"}</div>
          <div style={{ marginTop: 4, ...small }}>{consignee.address || ""}</div>
          <div style={{ marginTop: 4, ...small }}>GSTIN: {consignee.gstin || "-"}</div>
          <div style={{ marginTop: 4, ...small }}>PH: {consignee.phone || ""}</div>
        </div>
      </div>

      {/* Items table */}
      <div style={{ marginTop: 12 }}>
        <table className="inv-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>No</th>
              <th style={{ width: 120 }}>Description of Goods</th>
              <th style={{ width: 90 }}>HSN / SAC</th>
              <th style={{ width: 60 }} className="center">Qty</th>
              <th style={{ width: 60 }} className="center">UOM</th>
              <th style={{ width: 90 }} className="right">Unit Price</th>
              <th style={{ width: 90 }} className="right">Gross</th>
              <th style={{ width: 60 }} className="center">Disc %</th>
              <th style={{ width: 90 }} className="right">Taxable Value</th>
              <th style={{ width: 60 }} className="center">GST %</th>
              <th style={{ width: 90 }} className="right">GST Amt</th>
              <th style={{ width: 90 }} className="right">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? items.map((it, idx) => (
              <tr key={idx}>
                <td className="center">{it.sl ?? idx + 1}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{it.desc || ""}</div>
                  <div style={{ color: "#555", fontSize: 12 }}>{it.code ? `Code: ${it.code}` : ""}</div>
                </td>
                <td className="center">{it.hsn || ""}</td>
                <td className="center">{it.qty ?? ""}</td>
                <td className="center">{it.uom || ""}</td>
                <td className="right">{money(it.unitPrice || it.rate)}</td>
                <td className="right">{money(it.gross)}</td>
                <td className="center">{it.discountPercent ? `${it.discountPercent}%` : "-"}</td>
                <td className="right">{money(it.taxableValue)}</td>
                <td className="center">{it.gstPercent ?? (it.gst || "")}</td>
                <td className="right">{money(it.gstAmt)}</td>
                <td className="right">{money(it.total)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={12} className="center" style={{ padding: 20, color: "#666" }}>No items</td>
              </tr>
            )}
          </tbody>

          {/* foot: totals */}
          <tfoot>
            <tr>
              <td colSpan={6} style={{ border: "none" }}></td>
              <td className="right" style={{ fontWeight: 700 }}>{money(totals.gross)}</td>
              <td style={{ borderLeft: "1px solid #e8e8e8" }}></td>
              <td className="right" style={{ fontWeight: 700 }}>{money(totals.taxable)}</td>
              <td className="center">{/* GST col */}</td>
              <td className="right" style={{ fontWeight: 700 }}>{money((totals.cgst || 0) + (totals.sgst || 0) + (totals.igst || 0))}</td>
              <td className="right" style={{ fontWeight: 800 }}>{money(totals.grandTotal || totals.net || totals.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Amount in words and summary */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
        <div style={{ width: "60%" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Amt. In Words :</div>
          <div style={{ padding: 8, border: "1px dashed #e6e6e6", minHeight: 36 }}>{amountWords || ""}</div>

          {notes ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 700 }}>Remarks</div>
              <div style={{ padding: 8, border: "1px dashed #e6e6e6" }}>{notes}</div>
            </div>
          ) : null}
        </div>

        <div style={{ width: "36%" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              <tr>
                <td style={{ padding: 8 }}>Sub Total</td>
                <td style={{ padding: 8 }} className="right">{money(totals.subtotal || totals.gross)}</td>
              </tr>
              <tr>
                <td style={{ padding: 8 }}>Taxable Value</td>
                <td style={{ padding: 8 }} className="right">{money(totals.taxable)}</td>
              </tr>
              {!isInterState ? (
                <>
                  <tr>
                    <td style={{ padding: 8 }}>CGST</td>
                    <td style={{ padding: 8 }} className="right">{money(totals.cgst)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8 }}>SGST</td>
                    <td style={{ padding: 8 }} className="right">{money(totals.sgst)}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td style={{ padding: 8 }}>IGST</td>
                  <td style={{ padding: 8 }} className="right">{money(totals.igst)}</td>
                </tr>
              )}
              <tr>
                <td style={{ padding: 8 }}>Round Off</td>
                <td style={{ padding: 8 }} className="right">{money(totals.roundOff || 0)}</td>
              </tr>
              <tr style={{ fontWeight: 800, fontSize: 16 }}>
                <td style={{ padding: 8 }}>Invoice Total</td>
                <td style={{ padding: 8 }} className="right">{money(totals.grandTotal || totals.net || totals.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: bank details and signature */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, alignItems: "flex-end" }}>
        <div style={{ width: "60%", fontSize: 13 }}>
          <div style={{ fontWeight: 700 }}>OUR BANK</div>
          <div style={{ marginTop: 6 }}>{bank.acctName || company.name || ""}</div>
          <div>Account No: {bank.acctNo || ""}</div>
          <div>Bank: {bank.name || ""}, Branch: {bank.branch || ""}</div>
          <div>IFSC: {bank.ifsc || ""}</div>
        </div>

        <div style={{ width: "34%", textAlign: "right" }}>
          <div style={{ marginBottom: 40 }}>Authorised Signatory</div>
          <div style={{ fontWeight: 700 }}>{company.name || ""}</div>
        </div>
      </div>

    </div>
  );
}
